import moment from "moment";
import PropTypes from "prop-types";
import classNames from "classnames";
import React, { useState } from "react";
import { Button } from "@wattpad/web-ui-library";

import FormField from "./FormField";
import { useTrans } from "../../hooks";
import { Icon } from "../../shared-components";
import { isClient, sanitizeHTML } from "../../helpers";

const mapLanguagetoBetaLinks = {
  1: "https://support.wattpad.com/hc/en-us/articles/201708720-Becoming-a-Beta-tester", //English
  2: "https://support.wattpad.com/hc/fr/articles/201708720-Devenir-un-b%C3%AAta-testeur", //French
  3: "https://support.wattpad.com/hc/it/articles/201708720-Come-diventare-un-Beta-tester-per-Wattpad", // Italian
  4: "https://support.wattpad.com/hc/de/articles/201708720-Ein-Beta-Tester-werden", //German
  5: "https://support.wattpad.com/hc/es/articles/201708720-Convi%C3%A9rtete-en-un-usuario-Beta", //Spanish
  6: "https://support.wattpad.com/hc/pt/articles/201708720-Como-se-tornar-um-testador-Beta", //Portuguese
  18: "https://support.wattpad.com/hc/fil/articles/201708720-Pagiging-Beta-tester", //Filipino
  20: "https://support.wattpad.com/hc/id/articles/201708720-Menjadi-Beta-tester", //Indonesian
  23: "https://support.wattpad.com/hc/tr/articles/201708720-Beta-Kullan%C4%B1c%C4%B1s%C4%B1-Olma" //Turkish
};

const isBirthdateValid = birthdate => {
  if (!birthdate?.trim()) return false;

  const date = moment(birthdate, "YYYY-MM-DD", true);
  if (!date.isValid()) return false;

  // subtract/add 1 day to include the exact boundary
  const minDate = moment()
    .subtract(100, "years")
    .subtract(1, "day");
  const maxDate = moment()
    .subtract(13, "years")
    .add(1, "day");

  return date.isAfter(minDate, "day") && date.isBefore(maxDate, "day");
};

const AccountSettings = props => {
  const { trans } = useTrans();
  const [email, setEmail] = useState(props.email);
  const [isBetaEnabled, setIsBetaEnabled] = useState(props.beta_enabled);
  const [birthdateError, setBirthdateError] = useState("");
  const isSettingsDobValidation =
    window.wattpad?.testGroups?.IS_SETTINGS_DOB_VALIDATION;

  const { username, has_password, app, verified_email, birthdate } = props;
  const betaLink = mapLanguagetoBetaLinks[app.language]
    ? mapLanguagetoBetaLinks[app.language]
    : mapLanguagetoBetaLinks[1];

  const dobHelpLink =
    "https://support.wattpad.com/hc/articles/360000850226-Wattpad-age-requirements";

  const HelpIcon = isSettingsDobValidation ? (
    <Icon
      name="infoOutline"
      size="24"
      color="ds-neutral-100"
      strokeWidth="0"
      fill={true}
      className="tooltip-trigger"
    />
  ) : (
    <Icon
      iconName="fa-info"
      height="16"
      color="wp-neutral-2"
      className="tooltip-trigger"
    />
  );

  const onCheckboxChange = () => {
    setIsBetaEnabled(oldVal => !oldVal);
  };

  const validateBirthdate = dateValue => {
    if (!isBirthdateValid(dateValue)) {
      setBirthdateError(trans("Please fix any errors to continue."));
      return false;
    }

    setBirthdateError("");
    return true;
  };

  const handleSubmit = event => {
    event.preventDefault();
    if (isSettingsDobValidation) {
      const birthdateInput = document.getElementById("birthdate");
      if (!validateBirthdate(birthdateInput?.value)) {
        event.preventDefault();
        return;
      }
    }
    const userData = [{ name: "beta_enabled", value: isBetaEnabled }];
    props.onSubmit(event, userData);
  };

  const onUpdateEmail = newEmail => {
    setEmail(newEmail);
  };

  const openSettingsModal = (event, type) => {
    event.preventDefault();
    if (type == "email") {
      props.openEditModal(type, onUpdateEmail, email);
    } else {
      props.openEditModal(type);
    }
  };

  return (
    <div id="account">
      <h2 className="subheading">
        {trans("Change your account information and privacy settings")}
      </h2>
      <form id="account_form" name="account_form" onSubmit={handleSubmit}>
        <div className="row account-wrapper">
          <br />
          <FormField label={trans("Username")}>
            {username}
            <button
              className="info"
              id="changeUsername"
              onClick={event => openSettingsModal(event, "username")}
            >
              {trans("change")}
            </button>
          </FormField>
          <FormField label={trans("Password")}>
            {has_password && (
              <span>
                {sanitizeHTML("********")}
                <button
                  className="info"
                  id="changePassword"
                  onClick={event => openSettingsModal(event, "password")}
                >
                  {trans("change")}
                </button>
              </span>
            )}
            {!has_password && (
              <button
                className="info"
                id="addPassword"
                onClick={event => openSettingsModal(event, "addPassword")}
              >
                {trans("create password")}
              </button>
            )}
          </FormField>
          <FormField label={trans("Email")}>
            {email}
            <button
              className="info"
              id="changeEmail"
              onClick={event => openSettingsModal(event, "email")}
            >
              {trans("change")}
            </button>
            {!verified_email && (
              <div>
                {trans("Not Verified")}
                <a
                  className="info"
                  href={`user_activation?action=send&username=${username}`}
                >
                  {trans("Send Activation Code")}
                </a>
              </div>
            )}
          </FormField>
          <FormField label={trans("Date of Birth")} name="birthdate">
            <div className="birthdate-wrapper">
              {!isSettingsDobValidation && (
                <input
                  id="birthdate"
                  type="date"
                  name="birthdate"
                  max={moment().format("YYYY-MM-DD")}
                  defaultValue={moment(birthdate)
                    .locale("en")
                    .format("YYYY-MM-DD")}
                />
              )}
              {isSettingsDobValidation && (
                <>
                  <input
                    id="birthdate"
                    type="date"
                    name="birthdate"
                    min={moment()
                      .subtract(100, "years")
                      .format("YYYY-MM-DD")}
                    max={moment().format("YYYY-MM-DD")}
                    className={classNames({ error: birthdateError })}
                    defaultValue={moment(birthdate)
                      .locale("en")
                      .format("YYYY-MM-DD")}
                    onBlur={e => validateBirthdate(e.target.value)}
                    onInvalid={e => e.preventDefault()}
                    disabled={!!birthdate}
                  />

                  {!!birthdate && (
                    <span className="help">
                      <Icon
                        name="lockOutline"
                        size="20"
                        color="ds-neutral-100"
                        strokeWidth="0"
                        fill={true}
                      />
                    </span>
                  )}
                </>
              )}
              <span id="dob-tooltip" className="help">
                {HelpIcon}
              </span>
            </div>
          </FormField>
          {!!birthdate &&
            isSettingsDobValidation && (
              <div className="birthdate-info">
                {trans("Need help with your birthdate?")}
                <a
                  className="info"
                  href={dobHelpLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {trans("Visit the Help Center")}
                </a>
              </div>
            )}
          {isSettingsDobValidation &&
            birthdateError && (
              <div
                className="birthdate-error"
                role="alert"
                aria-live="assertive"
              >
                {birthdateError}
              </div>
            )}
          <FormField
            label={trans("Join Beta Program")}
            name="betaEnabled"
            value={isBetaEnabled}
            type="checkbox"
            onCheckboxChange={onCheckboxChange}
          >
            <a
              className="info"
              href={betaLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              {trans("Learn more")}
            </a>
            <span id="beta-tooltip" className="help">
              {HelpIcon}
            </span>
          </FormField>
          <FormField label={trans("Download your Personal Information")}>
            {/* eslint-disable-next-line wp-lint-rules/valid-href */}
            <a href="/settings/your-wattpad-data">{trans("HTML")}</a>
            {/* eslint-disable-next-line wp-lint-rules/valid-href */}
            <a
              href="/settings/your-wattpad-data?json=1"
              style={{ paddingLeft: "5px" }}
            >
              {trans("JSON")}
            </a>
            <span id="info-tooltip" className="help">
              {HelpIcon}
            </span>
          </FormField>
          <div className="submit">
            <Button label={trans("Submit")} />
          </div>
        </div>
      </form>
      <div className="close-account">
        <a href={"/close-account"}>{trans("Close Account")}</a>
      </div>
    </div>
  );
};

AccountSettings.propTypes = {
  username: PropTypes.string.isRequired,
  has_password: PropTypes.bool,
  name: PropTypes.string,
  email: PropTypes.string.isRequired,
  language: PropTypes.number.isRequired,
  verified_email: PropTypes.bool,
  birthdate: PropTypes.string,
  beta_enabled: PropTypes.bool.isRequired,
  app: PropTypes.object.isRequired,
  openEditModal: PropTypes.func,
  onSubmit: PropTypes.func,
  onUpdateEmail: PropTypes.func
};

if (isClient()) {
  _.extend(AccountSettings.propTypes, {
    openEditModal: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired
  });
}

export default AccountSettings;
